import { DateTime } from 'luxon';
import React, {
  useEffect, useMemo, useRef,useState
} from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import { IoIosArrowRoundUp } from 'react-icons/io';
import { RiResetLeftFill } from 'react-icons/ri';
import Markdown from 'react-markdown';

import APIGateway from '@/api';
import { AgentConfig } from '@/types/agent';
import { ChatMsgTypeEnum, ChatRoleEnum, CompletionResponseTypeEnum } from '@/types/chat';

import styles from './lowCodeChat.module.scss';
import { validateChatAvailability } from './utils/chatValidation';


type Props = {
  config: AgentConfig
};
type DebugInfo = {
  inputs?: unknown[];
  outputs?: unknown[];
  step: string;
};
type ChatMessage = {
  debug?: DebugInfo[];
  message: string;
  user: string;
};

function LowCodeChat( props: Props ) {
  const [ isProcessing, setIsProcessing ] = useState<boolean>( false );
  const [ messages, setMessages ] = useState<ChatMessage[]>( [] );
  const [ userText, setUserText ] = useState<string>( '' );

  const containerRef = useRef<HTMLDivElement>( null );

  const chatValidation = useMemo(
    () => validateChatAvailability( props.config ),
    [ props.config ]
  );

  const resetMessages = () => {
    setMessages( [] );
    setUserText( '' );
  };

  const handleKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( e.key === 'Enter' ) {
      sendMessage();
    }
  };

  const sendMessage = () => {
    setIsProcessing( true );
    const sendMessages = [];
    for ( let i = 0; i < messages.length; i++ ) {
      sendMessages.push( {
        authorId: messages[i].user,
        createdAt: DateTime.fromJSDate( new Date() ),
        updatedAt: DateTime.fromJSDate( new Date() ),
        messageId: 'dsada',
        content: {
          type: ChatMsgTypeEnum.TEXTUAL_CONTENT,
          text: messages[i].message
        }
      } );
    }

    sendMessages.push( {
      authorId: 'user',
      createdAt: DateTime.fromJSDate( new Date() ),
      updatedAt: DateTime.fromJSDate( new Date() ),
      messageId: 'dsada',
      content: { type: ChatMsgTypeEnum.TEXTUAL_CONTENT, text: userText }
    } );

    const chatUsers = [
      {
        displayName: 'test',
        id: 'user',
        type: ChatRoleEnum.USER
      },
      {
        displayName: 'assistant',
        id: 'assistant',
        type: ChatRoleEnum.ASSISTANT
      }
    ];
    const config = {
      id: props.config.agentId,
      description: 'lowcode',
      steps: props.config.steps
    };
    setMessages( [ ...messages, { user: 'user', message: userText } ] );
    setUserText( '' );

    APIGateway.agents
      .agentInvokeAidp(
        {
          context: {
            values: [
              { key: 'lowcode_agent', value: JSON.stringify( config ) },
              { key: 'debug', value: 'true' }
            ]
          },
          agentId: 'lowcode-agent',
          clientId: 'aidp-preview',
          messages: sendMessages,
          users: chatUsers
        }
      )
      .then( ( response ) => {
        const firstResponse = response[0];
        if ( firstResponse.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_RESULT ) {
          const content = firstResponse.messages[0].content as unknown as { data: { resp: string, debug: string } };
          setMessages( [
            ...messages,
            { user: 'user', message: userText },
            {
              user: 'assistant',
              message: content.data.resp,
              debug: JSON.parse( content.data.debug )
            }
          ] );
        }

        setIsProcessing( false );
      } );
  };

  useEffect( () => {
    setTimeout( () => {
      const element = containerRef.current;
      if ( element ) {
        element.scrollTop = element.scrollHeight;
      }
    }, 500 );
  }, [ messages ] );

  return (
    <div>
      <div className={ styles.messages } ref={ containerRef }>
        {chatValidation.isDisabled && (
          <div className={ styles.disabledOverlay } data-testid="low-code-chat-disabled-overlay">
            <div className={ styles.warningMessage } data-testid="low-code-chat-warning-message">
              Unable to try low-code agent. Additional inputs and context variables have been defined in the start node. Save and use the playground instead.
            </div>
          </div>
        )}
        {messages.map( ( item, index ) => (
          <div key={ index }>
            {item.user == 'user' && (
              <div className={ styles.usermessage }>
                <label>{item.user}:</label>
                {item.message}
              </div>
            )}
            {item.user == 'assistant' && (
              <div className={ styles.agentmessage }>
                {item.debug && (
                  <>
                    {item.debug.map( ( debugItem, debugIndex ) => (
                      <div key={ debugIndex } className={ styles.debugItem }>
                        <label>{debugItem.step}</label>
                        {JSON.stringify( debugItem.outputs )}
                      </div>
                    ) )}
                  </>
                )}
                <label>{item.user}:</label>
                <Markdown>{item.message}</Markdown>
              </div>
            )}
          </div>
        ) )}
        {isProcessing && (
          <div className={ styles.agentmessage }>
            <label>Assistant:</label>
            <AiOutlineLoading className={ styles.loadgif } />
          </div>
        )}
      </div>
      <div className={ styles.chatArea }>
        <input
          data-testid="low-code-chat-input"
          disabled={ chatValidation.isDisabled }
          onChange={ ( e ) => setUserText( e.target.value ) }
          onKeyDown={ handleKeyDown }
          type="text"
          value={ userText }
        >
        </input>
        <button
          className={ styles.resetButton }
          data-testid="low-code-chat-reset-button"
          disabled={ chatValidation.isDisabled }
          onClick={ resetMessages }
        >
          <RiResetLeftFill />
        </button>
        <button
          data-testid="low-code-chat-send-button"
          disabled={ chatValidation.isDisabled }
          onClick={ sendMessage }
        >
          <IoIosArrowRoundUp />
        </button>
      </div>
    </div>
  );
}
export default LowCodeChat;
